import { useController, useFormContext } from 'react-hook-form';
import {
	CERTIFICATION_FLAG_BY_PROOF,
	proofToggleFlags,
	type KycCertificationFlag,
	type KycProofFlag,
} from '@dealstack/shared/lib/common/business/kycCertification';
import type {
	NewJoinersAndAllocationsKycFormValues,
	NewJoinersAndAllocationsProofGroup,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/useNewJoinersAndAllocationsKYCController';

type ProofGroupValue = Partial<Record<KycProofFlag | KycCertificationFlag, boolean>>;

export interface ProofToggleGroup {
	isProofRequired: (flag: KycProofFlag) => boolean;
	isCertificationRequired: (flag: KycProofFlag) => boolean;
	toggleProof: (flag: KycProofFlag) => void;
	toggleCertification: (flag: KycProofFlag) => void;
}

/**
 * Drives one of the step's two proof groups. A certification is meaningless without the proof it
 * certifies, so switching a proof off switches it off and switching a proof on restores the
 * default, which is on — the same rule the repository enforces on write, applied here because this
 * step submits once at the end rather than per toggle.
 */
export const useNewJoinersAndAllocationsProofToggles = (
	group: NewJoinersAndAllocationsProofGroup
): ProofToggleGroup => {
	const { control } = useFormContext<NewJoinersAndAllocationsKycFormValues>();
	const { field } = useController({ control, name: group });
	const value: ProofGroupValue = field.value;

	// Statement body, not an expression: `onChange` returns a promise, and leaking it out of a
	// `() => void` makes callers (React's `act`, among others) treat the toggle as async.
	const setFlags = (changes: ProofGroupValue) => {
		field.onChange({ ...value, ...changes });
	};

	return {
		isProofRequired: (flag) => value[flag] ?? false,
		isCertificationRequired: (flag) => value[CERTIFICATION_FLAG_BY_PROOF[flag]] ?? false,
		toggleProof: (flag) => setFlags(proofToggleFlags(flag, !(value[flag] ?? false))),
		toggleCertification: (flag) => {
			const certificationFlag = CERTIFICATION_FLAG_BY_PROOF[flag];
			setFlags({ [certificationFlag]: !(value[certificationFlag] ?? false) });
		},
	};
};
